// Baselayer Background Checks — TanStack Query hooks for UCC filings (list /
// detail / edit-history + the draft + workflow mutations). Mirrors the
// background-check hook conventions.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  bgcUccFilingApi,
  type BgcUccFilingDraftRequest,
  type BgcUccFilingDto,
  type BgcUccFilingEditDto,
  type BgcUccFilingsListParams,
} from "@/api/backgroundChecks/bgcUccFilingApi";

// ============================== query keys ============================

export const bgcUccFilingKeys = {
  all: ["bgc-ucc-filings"] as const,
  list: (params?: BgcUccFilingsListParams) =>
    [
      "bgc-ucc-filings",
      "list",
      params?.dealId ?? null,
      params?.loanId ?? null,
      params?.status ?? null,
    ] as const,
  detail: (id: string) => ["bgc-ucc-filings", "detail", id] as const,
  edits: (id: string) => ["bgc-ucc-filings", "edits", id] as const,
};

// ============================== queries ===============================

export function useBgcUccFilings(params?: BgcUccFilingsListParams) {
  return useQuery<BgcUccFilingDto[]>({
    queryKey: bgcUccFilingKeys.list(params),
    queryFn: () => bgcUccFilingApi.list(params),
  });
}

export function useBgcUccFiling(id: string | undefined) {
  return useQuery<BgcUccFilingDto>({
    queryKey: bgcUccFilingKeys.detail(id ?? ""),
    queryFn: () => bgcUccFilingApi.get(id!),
    enabled: !!id,
  });
}

export function useBgcUccFilingEdits(id: string | undefined) {
  return useQuery<BgcUccFilingEditDto[]>({
    queryKey: bgcUccFilingKeys.edits(id ?? ""),
    queryFn: () => bgcUccFilingApi.edits(id!),
    enabled: !!id,
  });
}

// ============================== mutations =============================

export function useCreateBgcUccFiling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BgcUccFilingDraftRequest) => bgcUccFilingApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bgcUccFilingKeys.all });
    },
  });
}

function useUccFilingMutation<TArgs>(
  id: string,
  mutationFn: (args: TArgs) => Promise<BgcUccFilingDto>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bgcUccFilingKeys.detail(id) });
      qc.invalidateQueries({ queryKey: bgcUccFilingKeys.edits(id) });
      qc.invalidateQueries({ queryKey: bgcUccFilingKeys.list() });
    },
  });
}

export function useUpdateBgcUccFiling(id: string) {
  return useUccFilingMutation<BgcUccFilingDraftRequest>(id, (body) =>
    bgcUccFilingApi.update(id, body),
  );
}

export function useSubmitBgcUccFilingForApproval(id: string) {
  return useUccFilingMutation<void>(id, () =>
    bgcUccFilingApi.submitForApproval(id),
  );
}

export function useApproveBgcUccFiling(id: string) {
  return useUccFilingMutation<void>(id, () => bgcUccFilingApi.approve(id));
}

export function useRejectBgcUccFiling(id: string) {
  return useUccFilingMutation<string>(id, (reason) =>
    bgcUccFilingApi.reject(id, reason),
  );
}

export function useRecordBgcUccManualFilingId(id: string) {
  return useUccFilingMutation<string>(id, (baselayerFilingId) =>
    bgcUccFilingApi.recordManualFilingId(id, baselayerFilingId),
  );
}

export function useAttachBgcUccFilingPdf(id: string) {
  return useUccFilingMutation<string>(id, (documentId) =>
    bgcUccFilingApi.attachPdf(id, documentId),
  );
}
